import React, {useEffect, useMemo} from 'react';

import {useLocalesMgr, usePathname, useRoute} from 'coreHooks';
import useSeo from 'coreHooks/useSeo';
import {Head} from 'coreUtils/adapters/head';
import {absoluteUrl, PARSED_URL, parseURL} from 'coreUtils/url';
import {getConfig, isServer} from 'coreUtils/utils';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

import {IPageUrlsProps} from '../ISeoProps';

interface IHreflangEntry {
	hreflang: string | null;
	href: string;
}

const HREFLANG_SELECTOR = 'link[rel="alternate"][as="document"]';

const syncHreflangLinks = (entries: IHreflangEntry[]): void => {
	document.head.querySelectorAll(HREFLANG_SELECTOR).forEach((el) => el.remove());

	entries.forEach(({hreflang, href}) => {
		const el = document.createElement('link');

		el.setAttribute('rel', 'alternate');

		if (hreflang != null) {
			el.setAttribute('hreflang', hreflang);
		}

		el.setAttribute('href', href);
		el.setAttribute('as', 'document');
		document.head.appendChild(el);
	});
};

const HrefLangs = ({urls}: IPageUrlsProps) => {
	const {managerRepository} = useGeneralProvider();
	const seo = useSeo();
	const localesMgr = useLocalesMgr();
	const currentLocale = localesMgr.getCurrentLocale();
	const currentLanguage = currentLocale.getLanguageCode();
	const route = useRoute();
	const pathname = usePathname();
	const isHomePage = route.isHomePage;
	const shouldRemoveHreflang = useMemo(() => {
		if (route.is404 || seo.getIsNoIndex()) {
			return true;
		} else {
			const canonicalURLString = seo.getCanonicalURL();

			if (!canonicalURLString) {
				return true;
			}

			let canonicalURL: PARSED_URL;

			try {
				canonicalURL = parseURL(canonicalURLString);
			} catch (e) {
				console.error(canonicalURLString, 'Wrong canonical url format');

				return true;
			}

			if (!canonicalURL?.pathname) {
				return true;
			}

			// Normalize both paths by removing locale prefix before comparison
			// This ensures home page canonical URLs (e.g., "/") match pathname (e.g., "/en-us/")
			const normalizedPathname = localesMgr.getUrlWithoutLocale(pathname);
			const normalizedCanonicalPathname = localesMgr.getUrlWithoutLocale(canonicalURL.pathname);

			return normalizedPathname !== normalizedCanonicalPathname;
		}
	}, [pathname, route.is404, seo.getCanonicalURL()]);

	// url without country and language
	const defaultUrl = useMemo(() => {
		return (
			absoluteUrl(localesMgr.updateLinkWithLocale(urls[currentLanguage], currentLocale))
				.split('/')
				.slice(0, 3)
				.join('/') + '/'
		);
	}, [urls, currentLanguage, currentLocale]);

	const {app: appConfig} = getConfig();
	const externalHreflangs = appConfig?.seo?.externalHreflangs || [];

	const entries: IHreflangEntry[] = useMemo(() => {
		if (Object.keys(urls).length === 0 || shouldRemoveHreflang) {
			return [];
		}

		const countries = managerRepository.getCountriesMgr().getCountries();
		const hreflangs = [];

		for (const country of countries) {
			const allowedLocales = country.getHreflangLanguageCodes();

			if (allowedLocales.length === 0) {
				continue;
			}

			allowedLocales.forEach((locale) => {
				let localeModel;

				try {
					localeModel = localesMgr.getLocale(country.getCountryCode(), locale);
				} catch (e) {
					if (!(e instanceof ReferenceError)) {
						throw e;
					} else {
						console.warn(e);

						return;
					}
				}

				let url = absoluteUrl(localesMgr.updateLinkWithLocale(urls[locale], localeModel));

				if (url) {
					url = url.split('?')[0];
					hreflangs.push({
						hreflang: localeModel.getHreflangCode(),
						href: decodeURI(url),
					});
				}
			});
		}

		externalHreflangs.forEach((entry) => {
			if (!entry.homepageOnly || isHomePage) {
				hreflangs.push({hreflang: entry.hreflang, href: entry.href});
			}
		});

		if (isHomePage) {
			hreflangs.push({hreflang: 'x-default', href: defaultUrl});
		}

		return hreflangs;
	}, [currentLocale.getCode(), urls, shouldRemoveHreflang, isHomePage, defaultUrl]);

	useEffect(() => {
		syncHreflangLinks(entries);
	}, [entries]);

	if (isServer) {
		return (
			<Head>
				{entries.map(({hreflang, href}) => (
					<link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} as="document" />
				))}
			</Head>
		);
	}

	return null;
};

export default HrefLangs;
